import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const nav = [
  { to: "/how-it-works", label: "How it works" },
  { to: "/for-insurance", label: "Insurance" },
  { to: "/for-banking", label: "Banking" },
  { to: "/for-coops-and-associations", label: "Co-ops" },
  { to: "/markets", label: "Markets" },
  { to: "/about", label: "About" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#f4f0e6] lg:bg-[#f4f0e6cc] lg:backdrop-blur-xl border-b border-[#0a19301a] text-[#0a1930]" : "bg-transparent text-[#f4f0e6]"}`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3" aria-label="Medicual — Home">
          <img src="/brand/medicual-logo.png" alt="Medicual — Medical Equal Care" className={`h-[42px] w-auto transition-[filter] duration-500 ${scrolled ? "" : "brightness-0 invert"}`} />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.replace(/\//g, "").replace(/-/g, "")}`} className={({ isActive }) => `overline-tag hover:text-[#f26522] transition-colors ${isActive ? "text-[#f26522]" : ""}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/contact" data-testid="header-contact-cta" className={`hidden lg:inline-flex items-center gap-2 px-5 py-2.5 overline-tag transition-colors ${scrolled ? "bg-[#0a1930] text-[#f4f0e6] hover:bg-[#f26522]" : "bg-[#f4f0e6] text-[#0a1930] hover:bg-[#f26522]"}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#f26522]" /> Contact us
          </Link>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} className="lg:hidden min-w-12 min-h-12 inline-flex items-center justify-center p-2.5 -mr-2" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}>
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ y: "-100%" }} animate={{ y: 0 }} exit={{ y: "-100%" }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="lg:hidden absolute inset-x-0 top-full h-[calc(100dvh-76px)] bg-[#0a1930] text-[#f4f0e6] px-6 py-10 overflow-y-auto overscroll-contain" data-testid="mobile-menu-panel">
            <div className="flex flex-col gap-6">
              {nav.map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.05, duration: 0.5 }}>
                  <NavLink to={n.to} data-testid={`mobile-nav-${n.to.replace(/\//g, "").replace(/-/g, "")}`} className="font-display text-4xl block hover:text-[#f26522] transition-colors">
                    <span className="overline-tag opacity-40 mr-3">{String(i + 1).padStart(2, "0")}</span>{n.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="hairline mt-4 bg-[#f4f0e6]" />
              <Link to="/contact" data-testid="mobile-contact-cta" className="inline-flex items-center gap-2 mt-2 overline-tag text-[#f26522]">→ Contact us</Link>
              <p className="overline-tag opacity-50 mt-8">Business inquiries · mj@medicual.com</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
